import React, { useCallback, useEffect, useMemo, useState } from 'react';

const API = '/api/opo/admin/learning';

function sourceOf(item) {
  return item?.source || 'OptiLaw';
}

function groupBySource(pending, approved) {
  const names = new Set();
  pending.forEach((item) => names.add(sourceOf(item)));
  approved.forEach((item) => names.add(sourceOf(item)));
  if (names.size === 0) names.add('OptiLaw');
  return [...names].sort((a, b) => {
    if (a === 'OptiLaw') return -1;
    if (b === 'OptiLaw') return 1;
    return a.localeCompare(b);
  }).map((source) => ({
    source,
    pending: pending.filter((item) => sourceOf(item) === source),
    approved: approved.filter((item) => sourceOf(item) === source),
  }));
}

function matchesFilter(item, filter) {
  if (!filter) return true;
  const hay = `${item.query || ''} ${item.originalQuery || ''} ${item.response || ''}`.toLowerCase();
  return hay.includes(filter);
}

const TRAINING_DOCS = [
  ['OPO Training Plan', '/home/optiprime/projects/nodes/Docs/00-CRITICAL/OPTILAW_OPO_TRAINING_PLAN.md'],
  ['Training Data Inventory', '/home/optiprime/projects/nodes/Docs/02-SERVICES/OPO_SERVICE/OPO_TRAINING_DATA_INVENTORY.md'],
  ['Training Guide', '/home/optiprime/projects/nodes/Docs/02-SERVICES/OPO_SERVICE/OPO_TRAINING_GUIDE.md'],
];

function TrainingDataPanel() {
  const [open, setOpen] = useState({});
  const toggle = (key) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="opo-training-panel">
      <div className="opo-location-header">
        <h3>OPO Training Data Sources</h3>
        <p className="opo-location-desc">OPO is being trained on curated, public-facing data to become the intelligent operator for the OptiPrime platform. Below are all the data sources that inform OPO&apos;s knowledge and capabilities.</p>
      </div>

      <TrainingSection
        title="1. Service Endpoints & Capabilities"
        count="9 services"
        expanded={!!open.services}
        onToggle={() => toggle('services')}
      >
        <div className="opo-training-grid">
          <ServiceCard title="OptiPrime Dashboard" meta="Port 3000 • https://app.optiprime.ai" items={['User authentication & role-based dashboards', 'Property management & tenant portal', 'ConnectMe integration']} />
          <ServiceCard title="OptiLaw" meta="Port 8081 • https://app.optilaw.ai" items={['BC real estate law research (RTA, Strata)', 'Case law search (BCCA, BCSC, RTB)', 'Citation verification & legal guidance']} />
          <ServiceCard title="ConnectMe" meta="Port 8084" items={['Unified calendar view & meeting scheduling', 'Email communication hub', 'Contact management']} />
          <ServiceCard title="OptiLens" meta="Port 6001 • https://lens.optiprime.ai" items={['Document capture (PWA)', 'OCR processing & categorization', 'Mobile-friendly scanning']} />
          <ServiceCard title="Optimum Realty Admin" meta="Port 8082 • Admin Only" items={['Gmail & Google Drive integration', 'Calendar synchronization', 'Company account settings']} />
          <ServiceCard title="OptiQuery" meta="Port 4000 • Admin Only" items={['Advanced AI query interface', 'System monitoring dashboard', 'Data analytics & log analysis']} />
        </div>
      </TrainingSection>

      <TrainingSection title="2. Documentation Corpus" count="50+ documents" expanded={!!open.docs} onToggle={() => toggle('docs')}>
        <h4 className="opo-training-subhead">Critical Documentation</h4>
        <ul className="opo-training-list">
          <li>OPTIPRIME_PROJECT_OVERVIEW.md</li>
          <li>CURRENT_ARCHITECTURE.md</li>
          <li>PORT_MAPPING.md</li>
          <li>ENVIRONMENT_ROUTING_GUIDE.md</li>
        </ul>
        <h4 className="opo-training-subhead">Service Documentation</h4>
        <ul className="opo-training-list">
          <li>OptiLaw Quick Reference & Admin Dashboard</li>
          <li>Optimum Realty Admin Guide</li>
          <li>ConnectMe Service Documentation</li>
          <li>OptiQuery System Monitoring</li>
        </ul>
        <p className="opo-location-desc">Only public-safe portions are used in training. Internal architecture, credentials, and sensitive business data are excluded.</p>
      </TrainingSection>

      <TrainingSection title="3. FAQ & Knowledge Base" count="30+ Q&A pairs" expanded={!!open.faq} onToggle={() => toggle('faq')}>
        <p className="opo-learning-text"><strong>Q: </strong>What is OptiPrime?<br /><strong>A: </strong>An AI-powered property management platform for BC real estate professionals.</p>
        <p className="opo-learning-text"><strong>Q: </strong>How do I sign up?<br /><strong>A: </strong>Visit https://www.optiprime.ai/ and click &quot;Join Our Development Community&quot;.</p>
        <p className="opo-learning-text"><strong>Q: </strong>What is OptiLaw?<br /><strong>A: </strong>Our AI legal research assistant specializing in BC real estate law.</p>
        <p className="opo-location-desc">All FAQ responses are curated and approved for public disclosure.</p>
      </TrainingSection>

      <TrainingSection title="4. Multilingual Support" count="29 languages via Qwen2.5 3B" expanded={!!open.languages} onToggle={() => toggle('languages')}>
        <div className="opo-training-lang-grid">
          {['English', 'French', 'Chinese (Simplified & Traditional)', 'Arabic', 'Tagalog', 'Korean', 'Spanish', 'Farsi', 'German', 'Hindi', 'Vietnamese', 'Japanese', 'Russian', 'Ukrainian', '+ 15 more languages'].map((lang) => (
            <span key={lang} className="opo-module-badge">{lang}</span>
          ))}
        </div>
        <p className="opo-location-desc">OPO automatically detects user language and responds accordingly.</p>
      </TrainingSection>

      <TrainingSection title="5. Security & Boundary Training" count="4 threat categories" expanded={!!open.security} onToggle={() => toggle('security')}>
        <h4 className="opo-training-subhead">Prompt Injection Detection</h4>
        <p className="opo-learning-text">Recognizes attempts to override system instructions</p>
        <h4 className="opo-training-subhead">Data Exfiltration Prevention</h4>
        <p className="opo-learning-text">Blocks attempts to extract user data or system information</p>
        <h4 className="opo-training-subhead">Social Engineering Defense</h4>
        <p className="opo-learning-text">Identifies impersonation and unauthorized access attempts</p>
        <h4 className="opo-training-subhead">Reconnaissance Mitigation</h4>
        <p className="opo-learning-text">Prevents disclosure of technical architecture details</p>
        <p className="opo-location-desc">All suspicious patterns are logged and flagged for security review.</p>
      </TrainingSection>

      <h3 className="opo-training-status-title">Training Status</h3>
      <div className="opo-activity-summary">
        {[
          ['Service Endpoints', 'Documented'],
          ['Feature Routes', 'In Progress'],
          ['Documentation', 'Needs Ingestion'],
          ['FAQ Responses', 'Documented'],
          ['Multilingual', 'Implemented'],
          ['Security Patterns', 'Documented'],
        ].map(([label, value]) => (
          <div key={label} className="opo-summary-card">
            <span>{label}</span>
            <strong>{value}</strong>
          </div>
        ))}
      </div>

      <section className="opo-location-card">
        <div className="opo-location-header">
          <h3>Reference Documents</h3>
        </div>
        <ul className="opo-training-docs">
          {TRAINING_DOCS.map(([label, path]) => (
            <li key={path}>
              <strong>{label}: </strong>
              <code>{path}</code>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function TrainingSection({ title, count, expanded, onToggle, children }) {
  return (
    <section className="opo-location-card opo-training-section">
      <button type="button" className="opo-training-header" onClick={onToggle}>
        <h3>{expanded ? '▼' : '▶'} {title}</h3>
        <span className="opo-module-badge">{count}</span>
      </button>
      {expanded && <div className="opo-training-body">{children}</div>}
    </section>
  );
}

function ServiceCard({ title, meta, items }) {
  return (
    <article className="opo-stat-card">
      <h4 className="opo-training-subhead">{title}</h4>
      <p className="opo-location-desc">{meta}</p>
      <ul className="opo-training-list">
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </article>
  );
}

const OPOLearningTab = () => {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [listTab, setListTab] = useState('pending');
  const [view, setView] = useState('reviews');
  const [editingId, setEditingId] = useState(null);
  const [editedResponse, setEditedResponse] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processingId, setProcessingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, pendingRes, approvedRes] = await Promise.all([
        fetch(`${API}/stats`),
        fetch(`${API}/pending?minCount=1&limit=500`),
        fetch(`${API}/approved`),
      ]);
      if (statsRes.status === 401 || pendingRes.status === 401) {
        throw new Error('Sign in again to review learning patterns.');
      }
      const statsData = await statsRes.json();
      const pendingData = await pendingRes.json();
      const approvedData = await approvedRes.json();
      if (!statsData.success) throw new Error(statsData.error || 'Could not load learning stats');
      setStats(statsData.stats);
      setPending(pendingData.patterns || []);
      setApproved(approvedData.patterns || []);
    } catch (err) {
      setError(err.message || 'Could not load learning data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(
    () => groupBySource(pending, approved),
    [pending, approved]
  );
  const needle = filter.trim().toLowerCase();

  async function approve(patternId, response) {
    setProcessingId(patternId);
    try {
      const res = await fetch(`${API}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patternId,
          editedResponse: response == null ? null : response,
          actions: [],
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Approve failed');
      setEditingId(null);
      setEditedResponse('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessingId(null);
    }
  }

  async function reject(patternId) {
    setProcessingId(patternId);
    try {
      const res = await fetch(`${API}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patternId, reason: rejectReason.trim() }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Reject failed');
      setRejectingId(null);
      setRejectReason('');
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessingId(null);
    }
  }

  return (
    <div className="opo-learning-tab">
      <div className="opo-overview-header">
        <div>
          <h2>Learning</h2>
          <p className="opo-section-desc">Review replies before they are saved for instant answers. Each source stays in its own group.</p>
        </div>
        <button type="button" className="opo-learning-btn" onClick={load} disabled={loading}>Refresh</button>
      </div>

      <div className="opo-cornelius-subtabs">
        <button type="button" className={`opo-sub-tab-button ${view === 'reviews' ? 'opo-sub-tab-active' : ''}`} onClick={() => setView('reviews')}>
          Reviews
        </button>
        <button type="button" className={`opo-sub-tab-button ${view === 'training' ? 'opo-sub-tab-active' : ''}`} onClick={() => setView('training')}>
          Training Data
        </button>
      </div>

      {view === 'training' && <TrainingDataPanel />}

      {view === 'reviews' && stats && (
        <div className="opo-activity-summary">
          <div className="opo-summary-card"><span>Logged</span><strong>{stats.totalLogged ?? 0}</strong></div>
          <div className="opo-summary-card"><span>Pending</span><strong>{stats.pendingReview ?? 0}</strong></div>
          <div className="opo-summary-card"><span>Approved</span><strong>{stats.approvedPatterns ?? 0}</strong></div>
          <div className="opo-summary-card"><span>Rejected</span><strong>{stats.rejected ?? 0}</strong></div>
        </div>
      )}

      {view === 'reviews' && (
      <div className="opo-activity-filters">
        <label>
          Find
          <input
            className="opo-learning-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search questions and replies"
          />
        </label>
      </div>
      )}

      {view === 'reviews' && error && <p className="opo-error">{error}</p>}
      {view === 'reviews' && (loading && !stats ? (
        <div className="opo-loading-container"><div className="opo-loading-spinner" /></div>
      ) : error ? null : (
        groups.map((group) => {
          const pendingShown = group.pending.filter((item) => matchesFilter(item, needle));
          const approvedShown = group.approved.filter((item) => matchesFilter(item, needle));
          return (
            <section key={group.source} className="opo-location-card opo-learning-source">
              <div className="opo-location-header">
                <h3>{group.source}</h3>
                <p className="opo-location-desc">
                  {group.source === 'OptiLaw'
                    ? 'Transferred from the OptiLaw admin review.'
                    : 'New questions logged for this source.'}
                  {' '}
                  {group.pending.length} pending, {group.approved.length} approved.
                </p>
              </div>
              <div className="opo-cornelius-subtabs">
                <button
                  type="button"
                  className={`opo-sub-tab-button ${listTab === 'pending' ? 'opo-sub-tab-active' : ''}`}
                  onClick={() => setListTab('pending')}
                >
                  Pending ({pendingShown.length})
                </button>
                <button
                  type="button"
                  className={`opo-sub-tab-button ${listTab === 'approved' ? 'opo-sub-tab-active' : ''}`}
                  onClick={() => setListTab('approved')}
                >
                  Approved ({approvedShown.length})
                </button>
              </div>

              {listTab === 'pending' && (
                pendingShown.length === 0
                  ? <p className="opo-empty">No pending reviews in {group.source}.</p>
                  : pendingShown.map((pattern) => (
                    <article key={pattern.id} className="opo-stat-card opo-learning-card">
                      <div className="opo-learning-card-meta">
                        <span className="opo-module-badge">{pattern.category || 'GENERAL'}</span>
                        <span className="opo-module-badge">{pattern.language || 'en'}</span>
                        <span className="opo-module-badge">{pattern.count || 1}×</span>
                      </div>
                      <p className="opo-learning-label">Question</p>
                      <p className="opo-learning-text">{pattern.query}</p>
                      <p className="opo-learning-label">Reply</p>
                      {editingId === pattern.id ? (
                        <textarea
                          className="opo-learning-edit"
                          rows={6}
                          value={editedResponse}
                          onChange={(e) => setEditedResponse(e.target.value)}
                        />
                      ) : (
                        <p className="opo-learning-text">{pattern.response}</p>
                      )}
                      {rejectingId === pattern.id && (
                        <label className="opo-learning-reason">
                          Reason for rejecting
                          <input
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Why this reply should not be saved"
                          />
                        </label>
                      )}
                      <div className="opo-learning-actions">
                        {editingId === pattern.id ? (
                          <>
                            <button type="button" className="opo-learning-btn opo-learning-btn-primary" disabled={processingId === pattern.id} onClick={() => approve(pattern.id, editedResponse)}>
                              Save and approve
                            </button>
                            <button type="button" className="opo-learning-btn" onClick={() => setEditingId(null)}>Cancel</button>
                          </>
                        ) : rejectingId === pattern.id ? (
                          <>
                            <button type="button" className="opo-learning-btn opo-learning-btn-danger" disabled={processingId === pattern.id || !rejectReason.trim()} onClick={() => reject(pattern.id)}>
                              Confirm reject
                            </button>
                            <button type="button" className="opo-learning-btn" onClick={() => { setRejectingId(null); setRejectReason(''); }}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button type="button" className="opo-learning-btn opo-learning-btn-primary" disabled={processingId === pattern.id} onClick={() => approve(pattern.id, null)}>
                              Quick approve
                            </button>
                            <button type="button" className="opo-learning-btn" onClick={() => { setEditingId(pattern.id); setEditedResponse(pattern.response || ''); setRejectingId(null); }}>
                              Edit and approve
                            </button>
                            <button type="button" className="opo-learning-btn opo-learning-btn-danger" onClick={() => { setRejectingId(pattern.id); setRejectReason(''); setEditingId(null); }}>
                              Reject
                            </button>
                          </>
                        )}
                      </div>
                    </article>
                  ))
              )}

              {listTab === 'approved' && (
                approvedShown.length === 0
                  ? <p className="opo-empty">No approved replies in {group.source}.</p>
                  : (
                    <div className="opo-learning-approved-list">
                      {approvedShown.map((pattern) => (
                        <article key={pattern.id} className="opo-stat-card opo-learning-card">
                          <div className="opo-learning-card-meta">
                            <span className="opo-module-badge">{pattern.category || 'GENERAL'}</span>
                            {pattern.approvedAt && (
                              <span className="opo-last-update">{new Date(pattern.approvedAt).toLocaleString()}</span>
                            )}
                          </div>
                          <p className="opo-learning-label">Question</p>
                          <p className="opo-learning-text">{pattern.originalQuery || pattern.query}</p>
                          <p className="opo-learning-label">Saved reply</p>
                          <p className="opo-learning-text">{pattern.response}</p>
                        </article>
                      ))}
                    </div>
                  )
              )}
            </section>
          );
        })
      ))}
    </div>
  );
};

export default OPOLearningTab;
